/**
 * Lightweight per-session activity feed.
 * - In-memory ring buffer (last 20 events) scoped to the active session id.
 * - Mirrored to localStorage so a host refresh keeps recent context.
 * - Pure helpers: components subscribe via the Zustand store.
 */

const MAX_EVENTS = 20;
const STORAGE_KEY = (sessionId) => `splitsnap_activity_${sessionId}`;

export const ActivityType = {
  JOIN:           'join',
  LEAVE:          'leave',
  CLAIM:          'claim',
  UNCLAIM:        'unclaim',
  SHARE:          'share',
  PAYMENT_SENT:   'payment_sent',
  PAYMENT_CONFIRMED: 'payment_confirmed',
  SYSTEM:         'system',
};

const ICONS = {
  join:               '👋',
  leave:              '👋',
  claim:              '✅',
  unclaim:            '↩️',
  share:              '🍕',
  payment_sent:       '💸',
  payment_confirmed:  '💰',
  system:             '⚙️',
};

export const iconFor = (type) => ICONS[type] || '•';

export const loadActivity = (sessionId) => {
  if (!sessionId) return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY(sessionId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(-MAX_EVENTS) : [];
  } catch { return []; }
};

export const persistActivity = (sessionId, events) => {
  if (!sessionId) return;
  try {
    localStorage.setItem(STORAGE_KEY(sessionId), JSON.stringify(events.slice(-MAX_EVENTS)));
  } catch { /* quota exceeded */ }
};

export const clearActivity = (sessionId) => {
  if (!sessionId) return;
  try { localStorage.removeItem(STORAGE_KEY(sessionId)); } catch {}
};

export const appendActivity = (existing, event) => {
  const next = [...existing, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, ts: Date.now(), ...event }];
  return next.slice(-MAX_EVENTS);
};

export const relTime = (ts) => {
  const diff = Date.now() - ts;
  if (diff < 5_000)   return 'just now';
  if (diff < 60_000)  return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(ts).toLocaleDateString();
};